import { Component, Host, h, Prop, State, Element } from '@stencil/core';
import languages from '../../../global/languages';
import { LanguageLink } from '../../../types/site';

@Component({
  tag: 'sy-site-language-buttons',
  styleUrl: './sy-site-language-buttons.scss',
  assetsDirs: ['assets'],
  shadow: true,
})
export class SySiteLanguageButtons {
  @State() defaultLanguage;

  /**
   * Available languages with fallback links in case alternate page is not available
   */
  @Prop({ mutable: true }) languages: LanguageLink[];

  @Element() el;
  /**
   * Use to add path alternates urls
   */
  @Prop() appsPaths: string;
  /**
   * Type of language display
   */
  @Prop({ reflect: true }) type: 'circle' | 'regular' = 'regular';

  private getLinkPathname(link: string) {
    const absoluteUrlRegex = new RegExp('^(?:[a-z+]+:)?//', 'i'); // ref: https://stackoverflow.com/questions/10687099/how-to-test-if-a-url-string-is-absolute-or-relative
    if (link.match(absoluteUrlRegex)) {
      return new URL(link).pathname;
    }
    return new URL(link, window.location.href).pathname;
  }

  private isPathOfCurrentLanguage(pathname: string) {
    const pathLanguageRegex = /^(\/\w{2})(?:$|\/)/; // First directory consists of exactly 2 alphanumeric and is followed by / or end of string
    const currentPathLanguage = window.location.pathname.match(pathLanguageRegex)?.[1];
    const linkPathLanguage = pathname.match(pathLanguageRegex)?.[1];
    return currentPathLanguage === linkPathLanguage;
  }

  private observer: MutationObserver;

  private updateLanguageHrefs(alternateLinks: HTMLLinkElement[]) {
    const alternates = alternateLinks.filter((links) => links.rel === 'alternate');
    if (alternates.length > 0) {
      this.languages = this.languages.map((lang) => {
        const alternateLink = alternateLinks.find(
          (link) => link.hreflang.split('-')[0] === lang.hreflang.split('-')[0] && link.type !== 'application/rss+xml',
        );
        return alternateLink
          ? { ...lang, link: alternateLink.href }
          : this.defaultLanguage.find((defLang) => defLang.hreflang.split('-')[0] === lang.hreflang.split('-')[0]);
      });
    }
  }

  componentWillLoad() {
    this.languages = this.type === 'circle' ? languages.circleLanguages : languages.regularLanguages;
    this.defaultLanguage = this.languages;
    if (this.appsPaths === 'blog') {
      this.languages.forEach((lang) => {
        if (lang.link === '/') {
          lang.link += 'blog/';
        } else {
          lang.link += '/blog/';
        }
      });
    }
    if (this.appsPaths === 'marketplace') {
      this.languages.forEach((lang) => {
        if (lang.link === '/') {
          lang.link += 'marketplace/';
        } else {
          lang.link += '/marketplace/';
        }
      });
    }
    this.updateLanguageHrefs(Array.from(document.head.querySelectorAll('link[rel=alternate]')));
    this.observer = new MutationObserver((mutations: MutationRecord[]) => {
      mutations.forEach((mutation) => {
        const linkNodes = Array.from(mutation.addedNodes).filter((node) => node.nodeName === 'LINK');
        this.updateLanguageHrefs(linkNodes.map((node) => node as HTMLLinkElement));
      });
    });

    this.observer.observe(document.head, { childList: true });
  }

  render() {
    return (
      <Host>
        {this.languages?.map((language, index) => {
          return (
            <sy-router-link
              target="_self"
              class={{
                link: true,
                'circle-link': this.type === 'circle',
                active: this.isPathOfCurrentLanguage(this.getLinkPathname(language.link)),
              }}
              href={language.link}
              key={index}
            >
              <span class="inner-link">
                <sy-text type="small">{language.title}</sy-text>
              </span>
            </sy-router-link>
          );
        })}
      </Host>
    );
  }
}
